import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserInfo } from '../services/authService';
import { fetchMenuItems } from '../services/menuService';
import { changeNameService, fetchUserSettings, updateLocale } from '../services/userProfileService';
import { setDefaultDateFnsOptions } from '../utils/helpers';

const initialState = {
  id: '',
  initials: '',
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  locale: '',
  organisation: '',
  champLinkPrefix: '',
  created: 0,
  isAuthenticated: false,
  isManagingCustomer: false,
  tenantId: '',
  tenants: {},
  employeeId: '',
};

// Async thunks
export const getAuthUserData = createAsyncThunk(
  'user/getAuthUserData',
  async () => {
    const response = await getUserInfo();
    const { tenants, primary, userId } = response.data;
    return { id: userId, tenantId: primary, tenants };
  }
);

export const requestMenuData = createAsyncThunk(
  'user/requestMenuData',
  async () => {
    const response = await fetchMenuItems();
    const { menuName } = response.data;
    setDefaultDateFnsOptions({ locale: menuName.locale });
    return {
      initials: menuName.initials,
      name: menuName.name,
      firstName: menuName.firstName,
      lastName: menuName.lastName,
      email: menuName.email,
      locale: menuName.locale,
      organisation: menuName.organisation,
      champLinkPrefix: menuName.champLinkPrefix,
      created: menuName.created,
      isManagingCustomer: menuName.isManagingCustomer,
      employeeId: menuName.id,
    };
  }
);

export const requestUserSettings = createAsyncThunk(
  'user/requestUserSettings',
  async () => {
    const response = await fetchUserSettings();
    return response.data;
  }
);

export const changeName = createAsyncThunk(
  'user/changeName',
  async (name) => {
      const { data } = await changeNameService({ name });
      return { name, data };
  }
);

export const changeLocale = createAsyncThunk(
  'user/changeLocale',
  async (locale) => {
    const { data } = await updateLocale({ locale });
    window.CHAMP.locale.data.map = data.localeData.translations;
    window.CHAMP.locale.data.locale = data.localeData.code;
    return data;
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthenticated: (state) => {
      state.isAuthenticated = true;
    },
    authUserInfoReceived: (state, action) => {
      state.id = action.payload.id;
      state.tenantId = action.payload.tenantId;
      state.tenants = action.payload.tenants;
    },
    userInfoReceived: (state, action) => {
      state.initials = action.payload.initials;
      state.name = action.payload.name;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.email = action.payload.email;
      state.locale = action.payload.locale;
      state.organisation = action.payload.organisation;
      state.champLinkPrefix = action.payload.champLinkPrefix;
      state.created = action.payload.created;
      state.isManagingCustomer = action.payload.isManagingCustomer;
      state.employeeId = action.payload.employeeId;
    },
    userNameChanged: (state, action) => {
      state.name = action.payload.name;
    },
    userSettingsReceived: (state, action) => {
      state.locale = action.payload.data.locale;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAuthUserData.fulfilled, (state, action) => {
        state.id = action.payload.id;
        state.tenantId = action.payload.tenantId;
        state.tenants = action.payload.tenants;
      })
      .addCase(requestMenuData.fulfilled, (state, action) => {
        state.initials = action.payload.initials;
        state.name = action.payload.name;
        state.firstName = action.payload.firstName;
        state.lastName = action.payload.lastName;
        state.email = action.payload.email;
        state.locale = action.payload.locale;
        state.organisation = action.payload.organisation;
        state.champLinkPrefix = action.payload.champLinkPrefix;
        state.created = action.payload.created;
        state.isManagingCustomer = action.payload.isManagingCustomer;
        state.employeeId = action.payload.employeeId;
      })
      .addCase(requestUserSettings.fulfilled, (state, action) => {
        state.locale = action.payload.locale;
      })
      .addCase(changeName.fulfilled, (state, action) => {
        state.name = action.payload.name;
      })
      .addCase(changeLocale.fulfilled, (state, action) => {
        state.locale = action.payload.locale;
      })
      .addCase('IS_AUTHENTICATED', (state) => {
        state.isAuthenticated = true;
      })
      .addCase('AUTH_USER_INFO_RECEIVED', (state, action) => {
        state.id = action.id;
        state.tenantId = action.tenantId;
        state.tenants = action.tenants;
      })
      .addCase('USER_INFO_RECEIVED', (state, action) => {
        state.initials = action.initials;
        state.name = action.name;
        state.firstName = action.firstName;
        state.lastName = action.lastName;
        state.email = action.email;
        state.locale = action.locale;
        state.organisation = action.organisation;
        state.champLinkPrefix = action.champLinkPrefix;
        state.created = action.created;
        state.isManagingCustomer = action.isManagingCustomer;
        state.employeeId = action.employeeId;
      })
      .addCase('USER_NAME_CHANGED', (state, action) => {
        state.name = action.name;
      })
      .addCase('PUSER_SETTINGS_RECIEVED', (state, action) => {
        state.locale = action.data.locale;
      });
  },
});

export const { 
  setAuthenticated, 
  authUserInfoReceived, 
  userInfoReceived,
  userNameChanged,
  userSettingsReceived
} = userSlice.actions;

export default userSlice.reducer;

// Selectors
export const selectUser = (state) => state.user;